import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSite } from "@/contexts/SiteThemeContext";
import { track } from "@/lib/analytics";
import { usePins } from "@/lib/compareStore";

const COPY = {
  fr: {
    label: "Vérifier mon admissibilité",
    support: "Rapide • Sans obligation • 100 % confidentiel",
    eyebrow: "PROCHAINE ÉTAPE",
    title: "Prêt à voir vos options ?",
    body: "Quelques questions suffisent pour commencer.",
  },
  en: {
    label: "Check my eligibility",
    support: "Fast • No obligation • 100% confidential",
    eyebrow: "NEXT STEP",
    title: "Ready to see your options?",
    body: "A few questions are enough to get started.",
  },
};

export function ApplyCtaButton({
  placement,
  tone = "accent",
  size = "lg",
  fullWidth = false,
  className = "",
}) {
  const site = useSite();
  const { lang } = useLanguage();
  const { siteKey } = useParams();
  const copy = COPY[lang] || COPY.fr;
  const bg = tone === "primary" ? site.theme.primary : site.theme.accentWarm;

  return (
    <Button
      asChild
      size={size}
      className={`${fullWidth ? "w-full" : ""} font-bold uppercase tracking-wider text-white shadow-lg transition hover:-translate-y-0.5 ${className}`}
      style={{
        backgroundColor: bg,
        borderRadius: site.theme.radius,
        boxShadow: `0 12px 30px -8px ${bg}66`,
      }}
      data-testid={`apply-cta-${placement}`}
    >
      <Link
        to={`/${siteKey}/apply`}
        onClick={() => track("apply_cta_click", { placement, site: siteKey })}
      >
        {copy.label} <ArrowRight className="ml-2 h-4 w-4" />
      </Link>
    </Button>
  );
}

export function ApplyCtaPanel({ placement, compact = false, className = "" }) {
  const site = useSite();
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.fr;

  return (
    <section className={className} data-testid={`apply-cta-panel-${placement}`}>
      <div
        className={`relative overflow-hidden border ${compact ? "p-5 sm:p-6" : "p-6 sm:p-8"}`}
        style={{
          backgroundColor: site.theme.surfaceElevated || site.theme.surface,
          borderColor: site.theme.border,
          borderRadius: site.theme.radius,
          boxShadow: site.theme.shadow,
        }}
      >
        <div
          aria-hidden
          className="absolute right-0 top-0 h-28 w-28 rounded-full opacity-15"
          style={{
            background: `radial-gradient(circle, ${site.theme.accentWarm}, transparent 65%)`,
            transform: "translate(30%, -30%)",
          }}
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: site.theme.primary }}>
              <ShieldCheck className="h-3.5 w-3.5" /> {copy.eyebrow}
            </div>
            <h2 className={`${compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"} mt-2 font-black tracking-tight`} style={{ color: site.theme.text }}>
              {copy.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed sm:text-base" style={{ color: site.theme.muted }}>
              {copy.body}
            </p>
          </div>
          <div className="shrink-0 sm:text-right">
            <ApplyCtaButton placement={`${placement}-button`} fullWidth={compact} />
            <p className="mt-2 text-[11px] font-semibold" style={{ color: site.theme.muted }}>
              {copy.support}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function StickyMobileApplyCta() {
  const { pathname } = useLocation();
  const pins = usePins();

  if (pathname.endsWith("/apply")) return null;

  return (
    <div
      className="fixed inset-x-3 z-30 rounded-2xl border bg-white/95 p-2 shadow-2xl backdrop-blur sm:hidden"
      style={{
        bottom: pins.length ? "5.5rem" : "calc(env(safe-area-inset-bottom) + 0.75rem)",
        borderColor: "rgba(15, 23, 42, 0.12)",
      }}
      data-testid="sticky-mobile-apply-cta"
    >
      <ApplyCtaButton placement="sticky-mobile" fullWidth size="lg" />
    </div>
  );
}
